import { ProgressMode, ProgressSnapshot } from '../api/learner';
import { OBSERVATION_LABEL } from '../vocabulary/learningVocabulary';

/** "pendiente", never 0%: not enough history is not a score of nothing. */
export const PENDING_WORD = 'pendiente';

/**
 * What the card or the progress screen says the teacher noted, read off the
 * snapshot, never computed (S07-TR-01, SR-03).
 *
 * The same words the family reads, from the one vocabulary (DEC-S3-S07-03): same
 * version, same values. And it says whose words they are, about which class: no
 * policy turns a session's note into where a learner stands (DEC-S3-P09-02).
 */
export function observationLineOf(snapshot: ProgressSnapshot | undefined): string | undefined {
  if (!snapshot) return undefined;
  const latest = snapshot.latestObservation;
  if (snapshot.dataSufficiency === 'NOT_ENOUGH_HISTORY' || !latest) {
    return `Lo que anotó tu profesor: ${PENDING_WORD}`;
  }
  return `Lo que anotó tu profesor en la clase ${latest.sequence}: ${
    OBSERVATION_LABEL[latest.state]
  }`;
}

/**
 * MC01-R10 — the projection mode, as the server named it; from a server that
 * predates MC-01 v1-7, what the profile implies. A course with no profile is
 * SERVICE_ONLY, never LEP_2.
 */
export function progressModeOf(snapshot: ProgressSnapshot): ProgressMode {
  if (snapshot.progressMode) return snapshot.progressMode;
  switch (snapshot.learningProfile) {
    case 'LEP_0':
      return 'HISTORY_ONLY';
    case 'LEP_1':
      return 'SIMPLE_QUALITATIVE';
    case 'LEP_2':
      return 'STRUCTURED_OUTCOME';
    case 'LEP_3':
      return 'MASTERY_PROGRESSION';
    default:
      return 'SERVICE_ONLY';
  }
}

const isStructured = (mode: ProgressMode) =>
  mode === 'STRUCTURED_OUTCOME' || mode === 'MASTERY_PROGRESSION';

/**
 * Whether the card speaks of the teacher's note at all. Only where the teacher records an
 * observation (SIMPLE_QUALITATIVE) or a deferred structured profile does: in
 * HISTORY_ONLY and SERVICE_ONLY there is nothing to say, and "pendiente" there
 * would show a capability the course does not have as missing (MC01-R10).
 */
export function showsLearnerState(snapshot: ProgressSnapshot): boolean {
  const mode = progressModeOf(snapshot);
  return mode === 'SIMPLE_QUALITATIVE' || isStructured(mode);
}

/** Whether teacher feedback is part of this course at all (LEP_0 and SERVICE_ONLY: no). */
export function showsTeacherFeedback(snapshot: ProgressSnapshot): boolean {
  return showsLearnerState(snapshot);
}

/**
 * LEM-01 — milestone counts exist only for LEP_2 and LEP_3. For LEP_0 and
 * LEP_1 they are not shown even if a server sent them: experience and guided
 * practice are not measured in milestones.
 */
export function milestonesShown(
  snapshot: ProgressSnapshot,
): { completed: number; total: number } | null {
  if (!isStructured(progressModeOf(snapshot))) return null;
  return snapshot.milestones ?? null;
}
